import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDownIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { arrayHasItems } from '../../helpers/common';

/**
 * Lightweight searchable select optimized for medium/large option lists.
 * - Client-side tokenized search over label and value
 * - Keyboard navigation (ArrowUp/ArrowDown/Enter/Escape)
 * - Fully controlled `value` with simple `onChange`
 */
export const AccountSearchSelect = ({
  options = [],
  value = null,
  onChange,
  isLoading = false,
  placeholder = 'Select...',
  emptyText = 'No results found',
  containerClassName = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlighted, setHighlighted] = useState(0);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const normalizedOptions = useMemo(() => {
    return (options || []).map((opt) => ({
      ...opt,
      _search: `${String(opt.label || '')} ${String(opt.value || '')}`.toLowerCase(),
    }));
  }, [options]);

  const filtered = useMemo(() => {
    const q = String(query || '').trim().toLowerCase();
    if (!q) return normalizedOptions;
    const tokens = q.split(/\s+/).filter(Boolean);
    return normalizedOptions.filter((opt) => tokens.every((t) => opt._search.includes(t))).slice(0, 200);
  }, [normalizedOptions, query]);

  const selectedLabel = useMemo(() => {
    const found = (options || []).find((o) => o.value === value);
    return found?.label || placeholder;
  }, [options, value, placeholder]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    setHighlighted(0);
    // Focus search on open
    const t = setTimeout(() => inputRef.current?.focus(), 0);
    return () => clearTimeout(t);
  }, [isOpen, filtered.length]);

  const handleKey = (e) => {
    if (!isOpen) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlighted((h) => Math.min(h + 1, Math.max(filtered.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlighted((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const pick = filtered[highlighted];
      if (pick) {
        onChange?.(pick.value);
        setIsOpen(false);
        setQuery('');
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setIsOpen(false);
    }
  };

  return (
    <div className={`relative ${containerClassName}`} ref={containerRef} onKeyDown={handleKey}>
      <button
        type="button"
        disabled={isLoading}
        onClick={() => setIsOpen((p) => !p)}
        className={`w-full px-3 py-2 text-left bg-white flex items-center justify-between border border-gray-300 rounded-md shadow-sm disabled:cursor-not-allowed disabled:bg-[#efefef4d]`}
      >
        <span className="truncate">{selectedLabel}</span>
        <ChevronDownIcon className="h-4 w-4 ml-2 text-gray-600" />
      </button>

      {isOpen && (
        <div className="absolute z-20 w-full mt-2 bg-white border border-gray-300 rounded-md shadow-lg">
          <div className="relative">
            <MagnifyingGlassIcon className="h-4 w-4 text-gray-400 absolute left-2 top-1/2 -translate-y-1/2" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search accounts..."
              className="w-full px-3 py-2 pl-7 border-b border-gray-200 focus:outline-none text-sm"
            />
          </div>
          <ul className="max-h-56 overflow-y-auto">
            {arrayHasItems(filtered) ? (
              filtered.map((opt, idx) => (
                <li
                  key={`${opt.value}_${idx}`}
                  onMouseEnter={() => setHighlighted(idx)}
                  onClick={() => {
                    onChange?.(opt.value);
                    setIsOpen(false);
                    setQuery('');
                  }}
                  className={`px-3 py-2 text-left text-sm cursor-pointer whitespace-nowrap overflow-hidden text-ellipsis ${
                    idx === highlighted ? 'bg-blue-100' : 'hover:bg-gray-100'
                  }`}
                  title={opt.label}
                >
                  {opt.label}
                </li>
              ))
            ) : (
              <li className="px-3 py-2 text-sm text-gray-500">{emptyText}</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};


